import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Text,
  VStack,
  Image as ChakraImage,
  HoverCard,
  Collapsible,
  useDisclosure,
  Link,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { BsLayoutTextSidebarReverse, BsChevronDown } from "react-icons/bs";
import { useSelector } from "react-redux";
import img from "../../../assets/images/bbps.png";
import { userlogo } from "../../../assets/images/index";
import ProfileMenu from "components/includes/ProfileMenu/ProfileMenu";
import { RootState } from "store/rootReducer";
import {
  NavItem,
  navbarConfig,
  getVisibleNavbar,
  setAuthState,
} from "../Navbar/navConfig";

const Header: React.FC = () => {
  const { open, onToggle } = useDisclosure();
  const { isAuthenticated } = useSelector((s: RootState) => s.user);

  useEffect(() => {
    setAuthState(!!isAuthenticated);
  }, [isAuthenticated]);

  const visibleNav = getVisibleNavbar(navbarConfig);

  return (
    <Box
      as="header"
      bg="white"
      shadow="0 2px 10px rgba(255, 0, 30, 0.2)"
      position="relative"
      zIndex={99}
    >
      <Flex
        maxW="1200px"
        mx="auto"
        align="center"
        justify="space-between"
        px={4}
        py={3}
      >
        {/* Logo */}
        <RouterLink
          to="/"
          className="flex items-center gap-2 h-[30px] w-[90px]"
        >
          <ChakraImage src={img} alt="BBPS Logo" />
        </RouterLink>

        {/* Desktop Menu */}
        <Box as="nav" display={{ base: "none", md: "flex" }}>
          <HStack gap={6} as="ul" listStyleType="none" m={0} p={0}>
            {visibleNav.map((item) => (
              <RenderMenu key={item.id} item={item} />
            ))}
          </HStack>
        </Box>

        {/* Right side */}
        <HStack gap={2}>
          <Flex
            listStyleType="none"
            fontWeight="600"
            fontSize="md"
            color="gray.700"
            cursor="pointer"
            align="center"
            gap={1}
          >
            <Text>
              <Link href="/contectUs">Contact Us</Link>
            </Text>
          </Flex>
          <Flex
            listStyleType="none"
            fontWeight="600"
            fontSize="md"
            color="gray.700"
            cursor="pointer"
            align="center"
            gap={1}
            mr={[0, 4, 6]}
            ml={1}
          >
            <Text>
              <Link href="/aboutUs">About Us</Link>
            </Text>
          </Flex>
          {isAuthenticated ? (
            <>
              <ProfileMenu value="profile" />
            </>
          ) : (
            <RouterLink to="/auth" className="flex items-center gap-2">
              <ChakraImage src={userlogo} alt="user" boxSize="20px" />
              <Text fontSize="sm" fontWeight="medium">
                Login/SignUp
              </Text>
            </RouterLink>
          )}

          <IconButton
            aria-label="Toggle Menu"
            display={{ base: "flex", md: "none" }}
            onClick={onToggle}
            variant="ghost"
          >
            <BsLayoutTextSidebarReverse />
          </IconButton>
        </HStack>
      </Flex>

      {/* Mobile Nav */}
      <Collapsible.Root open={open} onOpenChange={onToggle}>
        <Collapsible.Content>
          <Box
            bg="gray.50"
            px={4}
            pb={4}
            display={{ base: "block", md: "none" }}
          >
            <VStack align="start" gap={3}>
              {visibleNav.map((item) =>
                item.children?.length ? (
                  <Box key={item.id} w="full">
                    <Text fontWeight="bold" mb={1}>
                      {item.label}
                    </Text>
                    <VStack align="start" pl={4}>
                      {item.children.map((child) => (
                        <RouterLink
                          key={child.id}
                          to={child.path ?? "#"}
                          className="text-sm"
                        >
                          {child.label}
                        </RouterLink>
                      ))}
                    </VStack>
                  </Box>
                ) : (
                  <RouterLink
                    key={item.id}
                    to={item.path ?? "#"}
                    className="text-sm"
                  >
                    {item.label}
                  </RouterLink>
                )
              )}
            </VStack>
          </Box>
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  );
};

export default Header;

// ---------------- Recursive Menu ----------------

const RenderMenu: React.FC<{ item: NavItem }> = ({ item }) => {
  const [open, setOpen] = useState(false);

  if (item.children && item.children.length > 0) {
    return (
      <HoverCard.Root
        open={open}
        onOpenChange={(details) => setOpen(details.open)}
        openDelay={0}
        closeDelay={100}
      >
        <HoverCard.Trigger asChild>
          <Flex
            as="li"
            listStyleType="none"
            fontWeight="600"
            fontSize="md"
            color="gray.700"
            cursor="pointer"
            align="center"
            gap={1}
          >
            <Text>{item.label}</Text>
            <Box
              as={BsChevronDown}
              boxSize={3}
              transition="transform 0.2s"
              transform={open ? "rotate(180deg)" : "rotate(0deg)"}
              strokeWidth="2"
            />
          </Flex>
        </HoverCard.Trigger>

        <HoverCard.Positioner>
          <HoverCard.Content
            p={3}
            bg="white"
            rounded="md"
            shadow="0 2px 8px rgba(59, 130, 246, 0.2)"
            zIndex={100}
            w="210px"
          >
            <HoverCard.Arrow>
              <HoverCard.ArrowTip />
            </HoverCard.Arrow>
            <VStack align="start" gap={2}>
              {item.children.map((child) =>
                child.children && child.children.length > 0 ? (
                  <RenderMenu key={child.id} item={child} />
                ) : (
                  <RouterLink
                    key={child.id}
                    to={child.path ?? "#"}
                    className="w-full text-sm hover:text-blue-500"
                  >
                    {child.label}
                  </RouterLink>
                )
              )}
            </VStack>
          </HoverCard.Content>
        </HoverCard.Positioner>
      </HoverCard.Root>
    );
  }

  return (
    <RouterLink
      to={item.path ?? "#"}
      className="font-semibold hover:color-blue-500 w-full hover:text-blue-500"
    >
      {item.label}
    </RouterLink>
  );
};

// import React, { useState } from "react";
// import { Link as RouterLink } from "react-router-dom";
// import {
//   Box,
//   Flex,
//   Text,
//   IconButton,
//   useDisclosure,
//   Collapsible,
//   Link,
//   Image,
//   HStack,
//   VStack,
//   HoverCard,
//   Button,
//   Menu,
//   Portal,
// } from "@chakra-ui/react";
// import { BsChevronDown, BsLayoutTextSidebarReverse } from "react-icons/bs";
// import { img } from "../../../assets/images/index";
// import { navbarConfig } from "../Navbar/navConfig";
// import { userlogo } from "../../../assets/images/index";
// import { LuChevronRight } from "react-icons/lu";
// import { useSelector } from "react-redux";
// import { RootState } from "store/rootReducer";
// import ProfileMenu from "components/includes/ProfileMenu/ProfileMenu";

// const Header = () => {
//   const { open, onToggle } = useDisclosure();
//   // const [activeMenu, setActiveMenu] = useState<string | null>(null);
//   const { isAuthenticated } = useSelector((s: RootState) => s.user);

//   return (
//     <Box
//       as="header"
//       bg="white"
//       shadow="0 2px 10px rgba(255, 0, 30, 0.2)"
//       position="relative"
//       zIndex={99}
//     >
//       <Flex
//         maxW="1200px"
//         mx="auto"
//         align="center"
//         justify="space-between"
//         px={4}
//         py={3}
//       >
//         {/* Logo */}
//         <RouterLink
//           to="/"
//           className="flex items-center gap-2 h-[30px] w-[90px]"
//         >
//           <Image src={img} alt="BBPS Logo" />
//         </RouterLink>

//         {/* Desktop Menu */}
//         <Box as="nav" display={{ base: "none", md: "flex" }}>
//           <HStack gap={6} as="ul" listStyleType="none" m={0} p={0}>
//             {navbarConfig.map((item) => (
//               <RenderMenu key={item.label} item={item} />
//             ))}
//           </HStack>
//         </Box>
//         <HStack gap={2}>
//           {isAuthenticated ? (
//             <ProfileMenu value={""} />
//           ) : (
//             <RouterLink to="/auth" className="flex items-center gap-2">
//               <Image src={userlogo} alt="user" boxSize="20px" />
//               <Text fontSize="sm" fontWeight="medium">
//                 Login/SignUp
//               </Text>
//             </RouterLink>
//           )}

//           <IconButton
//             aria-label="Toggle Menu"
//             display={{ base: "flex", md: "none" }}
//             onClick={onToggle}
//             variant="ghost"
//           >
//             <BsLayoutTextSidebarReverse />
//           </IconButton>
//         </HStack>
//       </Flex>

//       {/* Mobile Nav Collapse */}
//       <Collapsible.Root open={open} onOpenChange={onToggle}>
//         <Collapsible.Content>
//           <Box
//             bg="gray.50"
//             px={4}
//             pb={4}
//             display={{ base: "block", md: "none" }}
//           >
//             <VStack align="start" gap={3}>
//               {navbarConfig.map((item) =>
//                 item.children ? (
//                   <Box key={item.label} w="full">
//                     <Text fontWeight="bold" mb={1}>
//                       {item.label}
//                     </Text>
//                     <VStack align="start" pl={4}>
//                       {item.children.map((child) => (
//                         <Link key={child.label} href={child.path ?? "#"}>
//                           {child.label}
//                         </Link>
//                       ))}
//                     </VStack>
//                   </Box>
//                 ) : (
//                   <Link key={item.label} href={item.path ?? "#"}>
//                     {item.label}
//                   </Link>
//                 )
//               )}
//             </VStack>
//           </Box>
//         </Collapsible.Content>
//       </Collapsible.Root>
//     </Box>
//   );
// };

// export default Header;

// // Recursive menu component
// const RenderMenu = ({ item }: { item: any }) => {
//   const [open, setOpen] = useState(false);

//   if (item.children) {
//     return (
//       <HoverCard.Root
//         // positioning={{ placement: "left" }}
//         open={open}
//         onOpenChange={() => setOpen(!open)}
//         key={item.label}
//         openDelay={0}
//         closeDelay={100}
//       >
//         <HoverCard.Trigger asChild>
//           <Flex
//             as="li"
//             listStyleType="none"
//             fontWeight="600"
//             fontSize="md"
//             color="gray.700"
//             cursor="pointer"
//             align="center"
//             gap={1}
//           >
//             <Text>{item.label}</Text>
//             <Box
//               as={BsChevronDown}
//               boxSize={3}
//               transition="transform 0.2s"
//               transform={open ? "rotate(180deg)" : "rotate(0deg)"}
//               strokeWidth="2"
//             />
//           </Flex>
//         </HoverCard.Trigger>
//         <HoverCard.Positioner>
//           <HoverCard.Content
//             p={3}
//             bg="white"
//             shadow="0 2px 8px rgba(59, 130, 246, 0.2)" // subtle blue shadow
//             rounded="md"
//             zIndex={100}
//             w="210px"
//           >
//             <HoverCard.Arrow />
//             <VStack align="start" gap={2}>
//               {item.children.map((child: any) => (
//                 <RenderMenu key={child.label} item={child} />
//               ))}
//             </VStack>
//           </HoverCard.Content>
//         </HoverCard.Positioner>
//       </HoverCard.Root>
//     );
//   }
//   return (
//     <RouterLink
//       to={item.path ?? "#"}
//       className="font-semibold hover:color-blue-500 w-full hover:text-blue-500"
//     >
//       {item.label}
//     </RouterLink>
//   );
// };
